import { createFileRoute, useParams } from '@tanstack/react-router';
import { AlertCircle, CheckCircle2, Clock, ExternalLink, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { ProFeatureLock } from '@/features/billing/ui/pro-feature-lock';
import { useSyncJobs } from '@/features/sync';
import { useAnimationFlag } from '@/shared/lib/hooks/use-animation-flag';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';

import { AppLayout } from './-components/app-layout';
import { SegmentSettingsForm } from './-components/segment-settings-form';

export const Route = createFileRoute('/_authenticated/$mallId/settings')({
  component: SettingsPage,
});

const STATUS_CONFIG = {
  completed: { icon: CheckCircle2, label: '완료', className: 'text-green-600' },
  failed: { icon: AlertCircle, label: '실패', className: 'text-destructive' },
  running: { icon: Loader2, label: '진행 중', className: 'text-primary animate-spin' },
  pending: { icon: Clock, label: '대기', className: 'text-muted-foreground' },
} as const;

const TYPE_LABEL = { initial: '초기', incremental: '증분' } as const;

function formatDuration(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return '-';
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return '1초 미만';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}초`;
  const min = Math.floor(sec / 60);
  const remainSec = sec % 60;
  return remainSec > 0 ? `${min}분 ${remainSec}초` : `${min}분`;
}

const PAGE_SIZE = 10;

function SyncHistory({ mallId }: { mallId: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useSyncJobs(mallId, page, PAGE_SIZE);

  const totalPages = data ? Math.ceil(data.pagination.total / PAGE_SIZE) : 0;

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold">동기화 이력</h2>
      <div className="rounded-lg border overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            동기화 이력이 없습니다.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-2.5 font-medium">상태</th>
                    <th className="text-left px-4 py-2.5 font-medium">유형</th>
                    <th className="text-left px-4 py-2.5 font-medium">시작 시각</th>
                    <th className="text-left px-4 py-2.5 font-medium">소요 시간</th>
                    <th className="text-right px-4 py-2.5 font-medium">처리 건수</th>
                    <th className="text-left px-4 py-2.5 font-medium">비고</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((job) => {
                    const config = STATUS_CONFIG[job.status];
                    const Icon = config.icon;
                    return (
                      <tr key={job.id} className="border-b last:border-b-0 hover:bg-muted/30">
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center gap-1.5 ${config.className}`}>
                            <Icon className="w-3.5 h-3.5" />
                            {config.label}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground">
                          {TYPE_LABEL[job.syncType]}
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground">
                          {new Date(job.startedAt).toLocaleString('ko-KR')}
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground">
                          {formatDuration(job.startedAt, job.completedAt)}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                          {job.processedCount != null
                            ? `${job.processedCount.toLocaleString()}건`
                            : '-'}
                        </td>
                        <td className="px-4 py-2.5 max-w-48 truncate">
                          {job.status === 'failed' && job.errorMessage ? (
                            <span className="text-destructive text-xs" title={job.errorMessage}>
                              {job.errorMessage}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <span className="text-xs text-muted-foreground">총 {data.pagination.total}건</span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    이전
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    다음
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SettingsPage() {
  const { mallId } = useParams({ from: '/_authenticated/$mallId' });
  const { animationEnabled, toggleAnimation } = useAnimationFlag();

  return (
    <AppLayout pageTitle="설정">
      <div className="space-y-8 max-w-3xl mx-auto">
        <SyncHistory mallId={mallId} />

        <ProFeatureLock feature="customSegmentEnabled">
          <div className="space-y-6">
            <SegmentSettingsForm />
          </div>
        </ProFeatureLock>

        <div className="space-y-4">
          <h2 className="text-base font-semibold">애니메이션 설정</h2>
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="animation-toggle" className="text-sm font-medium">
                애니메이션 사용
              </Label>
              <p className="text-sm text-muted-foreground">
                차트와 UI 요소의 애니메이션 효과를 켜거나 끕니다.
              </p>
            </div>
            <Switch
              id="animation-toggle"
              checked={animationEnabled}
              onCheckedChange={toggleAnimation}
            />
          </div>
        </div>

        <div className="space-y-4">
          <h2 className="text-base font-semibold">법적 고지</h2>
          <div className="rounded-lg border p-4">
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
            >
              개인정보처리방침
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
